import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import i18next from "i18next";

import { IMessageThread } from "../../@types/MessageThread";
import {
  PatientMessagesTableContent,
  PatientRegisteredDevices,
} from "../../@types/Patient";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { messageService } from "../../services/messageService";
import { ensureOk } from "../mutations/messages";

interface IMessagesPaginatedResponse {
  messages: PatientMessagesTableContent[];
  total: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
}

export function textTruncate(response: string) {
  const length = 7;
  const ending = "...";

  if (response && response.length > length) {
    return response.substring(0, length - ending.length) + ending;
  }
  return response;
}

export function getMessageResponse(
  responseType: string,
  responseOptions: { value: string; selected: boolean }[]
) {
  // Check the responseType to return the required string response
  let response = "";

  if (responseType === "FreeText") {
    response = i18next.t("patientPage_messages_textResponse");
  } else if (responseType === "NoAnswer") {
    response = i18next.t("patientPage_messages_noAnswerRequired");
  } else if (responseOptions.length > 0) {
    const selectedResponse = responseOptions.find(
      (response) => response.selected === true
    );
    if (selectedResponse) {
      // Display the selected truncated response
      response = textTruncate(selectedResponse.value);
    }
  }

  return response;
}

/**
 * EN-3040. One conversation, already flattened by the API into the bubbles the modal renders.
 *
 * `threadId` accepts either a thread id or the id of any message in it, which is what lets the
 * table's deep link (?id=<messageId>) open the right conversation.
 */
export function useMessageThread(threadId: string | undefined) {
  const { data, isFetching, isFetchedAfterMount, isError, refetch } =
    useQuery<IMessageThread>({
      queryKey: ["message-thread", threadId],
      enabled: !!threadId,
      // The app-wide default is refetchOnMount: false with a 3 minute staleTime, which reopened a thread with
      // read states as they were last time. Opening the conversation is when staff expect it to be current.
      refetchOnMount: "always",
      // An error response must fail the query rather than be cached as the thread: the modal would
      // otherwise read `turns` off an ErrorResponse body and crash instead of showing "couldn't load".
      queryFn: async () =>
        ensureOk(await messageService.getThread(threadId as string), "load"),
    });

  return { thread: data, isFetching, isFetchedAfterMount, isError, refetch };
}

export function getPatientRegisteredDevices(patientId: number) {
  const { data, isFetching, isError, refetch, isRefetching } = useQuery<
    PatientRegisteredDevices[]
  >({
    queryKey: [`patient-registered-devices-${patientId}`, patientId],
    queryFn: async () => {
      if (!patientId) return [] as PatientRegisteredDevices[];

      const { data } = await messageService.getRegisteredDevices(patientId);

      return data.devices as PatientRegisteredDevices[];
    },
  });

  const PatientRegisteredDevices = data as PatientRegisteredDevices[];

  return {
    PatientRegisteredDevices,
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

/**
 * Hook for fetching patient messages with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch messages for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteMessages(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-messages-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<IMessagesPaginatedResponse> => {
      if (!patientId) {
        return {
          messages: [],
          total: 0,
          currentPage: 1,
          pageSize,
          hasNextPage: false,
        };
      }

      const response = await messageService.getMessagesPaginated(
        patientId,
        pageParam,
        pageSize
      );

      const { total, messages } = response.data;
      const totalPages = Math.ceil(total / pageSize);

      return {
        messages: messages as PatientMessagesTableContent[],
        total,
        currentPage: pageParam,
        pageSize,
        hasNextPage: pageParam < totalPages,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: IMessagesPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of messages
  const messages =
    data?.pages.flatMap((page: IMessagesPaginatedResponse) => {
      return (page.messages || []).map(
        (messageData: PatientMessagesTableContent) => {
          return {
            ...messageData,
            sentBy: messageData.sentBy,
            createdBy: messageData.createdByEmail,
            response: getMessageResponse(
              messageData.responseType,
              messageData.responseOptions
            ),
            messageBody: messageData.body,
            responseDate: messageData.responseDate,
            messageResponse: messageData.freeResponse,
          } as PatientMessagesTableContent;
        }
      );
    }) || [];

  // Get total count from the first page
  const totalCount = data?.pages[0]?.total ?? messages.length;

  return {
    messages,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}
